import { Col, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Control, Controller } from "react-hook-form";

import { DateTime } from "luxon";

import SelectCreatable from "@/common/components/SelectCreatable";
import { DiagnosticReferentialData } from "@/modules/dwelling/reducers";
import { DiagnosticTypeValue } from "@/modules/serviceOrdering/reducers";

type Option = {
  label: string;
  value: string;
};

const DwellingDiagnosticSecurityForm = ({
  control,
  slugs,
}: {
  control: Control<DiagnosticReferentialData, any>;
  slugs?: DiagnosticTypeValue[];
}) => {
  const OptionsAnomalies: Option[] = [
    { label: "Anomalies détectées", value: "anomaliesDetected" }, // true
    { label: "Aucune anomalie", value: "noAnomalies" }, // false
  ];

  const OptionsPresence: Option[] = [
    { label: "Présence", value: "presence" }, // true
    { label: "Absence", value: "absence" }, // false
  ];

  const resolveDefaulfSelectPresence = (value: boolean, options: Option[]) => {
    if (typeof value === "undefined" || value === null) return undefined;
    if (value) {
      return options.find((option) => option.value === "presence");
    } else {
      return options.find((option) => option.value === "absence");
    }
  };

  const resolveDefaulfSelectAnomalie = (value: boolean, options: Option[]) => {
    if (typeof value === "undefined" || value === null) return undefined;
    if (value) {
      return options.find((option) => option.value === "anomaliesDetected");
    } else {
      return options.find((option) => option.value === "noAnomalies");
    }
  };

  const resolveStringToBool = (value: string) => {
    if (value === "presence" || value === "anomaliesDetected") {
      return true;
    } else {
      return false;
    }
  };

  const handleOnChangeDate = (date: Date | null, dateTime: any) => {
    dateTime.field.onChange(
      (date ? DateTime.fromJSDate(date) : DateTime.now()).toISO(),
    );
  };

  return slugs?.includes(DiagnosticTypeValue.ELECTRICITY) ||
    slugs?.includes(DiagnosticTypeValue.GAS) ||
    slugs?.includes(DiagnosticTypeValue.ASBESTOS) ||
    slugs?.includes(DiagnosticTypeValue.LEAD) ||
    slugs?.includes(DiagnosticTypeValue.TERMITE) ? (
    <div className="mt-3">
      <div className="d-flex d-flex-row justify-content-between">
        <h2
          className="fw-bold main-title mb-3"
          style={{
            fontSize: "24px",
          }}
        >
          Sécurité
        </h2>
      </div>

      <div className="bg-container bg-container-form">
        <div className="d-flex flex-column flex-md-row flex-wrap gap-5">
          {/* Electrique */}
          {slugs?.includes(DiagnosticTypeValue.ELECTRICITY) ? (
            <Row className="w-100">
              <Row>
                <h4 className="add-prop-label mb-4">Electrique</h4>
              </Row>
              <Row>
                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Rapport d’intervention :
                  </label>
                  <Controller
                    control={control}
                    name="electricity.hasIssue"
                    render={(select) => (
                      <SelectCreatable
                        className="d-flex"
                        defaultValue={resolveDefaulfSelectAnomalie(
                          select.field.value,
                          OptionsAnomalies,
                        )}
                        options={OptionsAnomalies}
                        getValue={(value) =>
                          select.field.onChange(
                            resolveStringToBool(value.value),
                          )
                        }
                        isMulti={false}
                        creatable={false}
                        isNotSearchable={true}
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Date intervention
                  </label>
                  <Controller
                    control={control}
                    name="electricity.interventionDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : new Date()
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="w-100 add-prop-label ms-2">
                    Valide jusqu’au :
                  </label>
                  <Controller
                    control={control}
                    name="electricity.validityDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        placeholderText="Illimité"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : undefined
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>
              </Row>
            </Row>
          ) : undefined}

          {/* Gaz */}
          {slugs?.includes(DiagnosticTypeValue.GAS) ? (
            <Row className="w-100">
              <Row>
                <h4 className="add-prop-label mb-4">Gaz</h4>
              </Row>
              <Row>
                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Rapport d’intervention :
                  </label>
                  <Controller
                    control={control}
                    name="gas.hasIssue"
                    render={(select) => (
                      <SelectCreatable
                        className="d-flex"
                        defaultValue={resolveDefaulfSelectAnomalie(
                          select.field.value,
                          OptionsAnomalies,
                        )}
                        options={OptionsAnomalies}
                        getValue={(value) =>
                          select.field.onChange(
                            resolveStringToBool(value.value),
                          )
                        }
                        isMulti={false}
                        creatable={false}
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Date intervention
                  </label>
                  <Controller
                    control={control}
                    name="gas.interventionDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : new Date()
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="w-100 add-prop-label ms-2">
                    Valide jusqu’au :
                  </label>
                  <Controller
                    control={control}
                    name="gas.validityDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        placeholderText="Illimité"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : undefined
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>
              </Row>
            </Row>
          ) : undefined}

          {/* Amiante */}
          {slugs?.includes(DiagnosticTypeValue.ASBESTOS) ? (
            <Row className="w-100">
              <Row>
                <h4 className="add-prop-label mb-4">Amiante</h4>
              </Row>
              <Row>
                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Rapport d’intervention :
                  </label>
                  <Controller
                    control={control}
                    name="asbestos.isPresent"
                    render={(select) => (
                      <SelectCreatable
                        className="d-flex"
                        defaultValue={resolveDefaulfSelectPresence(
                          select.field.value,
                          OptionsPresence,
                        )}
                        options={OptionsPresence}
                        getValue={(value) =>
                          select.field.onChange(
                            resolveStringToBool(value.value),
                          )
                        }
                        isNotSearchable={true}
                        isMulti={false}
                        creatable={false}
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Date intervention
                  </label>
                  <Controller
                    control={control}
                    name="asbestos.interventionDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : new Date()
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="w-100 add-prop-label ms-2">
                    Valide jusqu’au :
                  </label>
                  <Controller
                    control={control}
                    name="asbestos.validityDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Illimité"
                        locale="fr"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : undefined
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>
              </Row>
            </Row>
          ) : undefined}

          {/* Plomb */}
          {slugs?.includes(DiagnosticTypeValue.LEAD) ? (
            <Row className="w-100">
              <Row>
                <h4 className="add-prop-label mb-4">Plomb</h4>
              </Row>
              <Row>
                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Rapport d’intervention :
                  </label>
                  <Controller
                    control={control}
                    name="lead.isPresent"
                    render={(select) => (
                      <SelectCreatable
                        className="d-flex"
                        defaultValue={resolveDefaulfSelectPresence(
                          select.field.value,
                          OptionsPresence,
                        )}
                        options={OptionsPresence}
                        getValue={(value) =>
                          select.field.onChange(
                            resolveStringToBool(value.value),
                          )
                        }
                        isNotSearchable={true}
                        isMulti={false}
                        creatable={false}
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Date intervention
                  </label>
                  <Controller
                    control={control}
                    name="lead.interventionDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : new Date()
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="w-100 add-prop-label ms-2">
                    Valide jusqu’au :
                  </label>
                  <Controller
                    control={control}
                    name="lead.validityDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        placeholderText="Illimité"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : undefined
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>
              </Row>
            </Row>
          ) : undefined}

          {/* Termite */}
          {slugs?.includes(DiagnosticTypeValue.TERMITE) ? (
            <Row className="w-100">
              <Row>
                <h4 className="add-prop-label mb-4">Termite</h4>
              </Row>
              <Row>
                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Rapport d’intervention :
                  </label>
                  <Controller
                    control={control}
                    name="termite.isPresent"
                    render={(select) => (
                      <SelectCreatable
                        className="d-flex"
                        defaultValue={resolveDefaulfSelectPresence(
                          select.field.value,
                          OptionsPresence,
                        )}
                        options={OptionsPresence}
                        getValue={(value) =>
                          select.field.onChange(
                            resolveStringToBool(value.value),
                          )
                        }
                        isNotSearchable={true}
                        isMulti={false}
                        creatable={false}
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="add-prop-label ms-2">
                    Date intervention
                  </label>
                  <Controller
                    control={control}
                    name="termite.interventionDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : new Date()
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>

                <Col md={3}>
                  <label className="w-100 add-prop-label ms-2">
                    Valide jusqu’au :
                  </label>
                  <Controller
                    control={control}
                    name="termite.validityDate"
                    render={(dateTime) => (
                      <DatePicker
                        className="add-prop-input date-select mt-2 mt-lg-0 w-100"
                        dateFormat="dd/MM/yyyy"
                        locale="fr"
                        placeholderText="Illimité"
                        selected={
                          dateTime.field.value
                            ? new Date(dateTime.field.value)
                            : undefined
                        }
                        onChange={(date) => handleOnChangeDate(date, dateTime)}
                        formatWeekDay={(nameOfDay) =>
                          nameOfDay.toString().toUpperCase().substr(0, 1)
                        }
                      />
                    )}
                  />
                </Col>
              </Row>
            </Row>
          ) : undefined}
        </div>
      </div>
    </div>
  ) : <></>;
};

export default DwellingDiagnosticSecurityForm;
